import type { ConversationRow } from '../chatv1/transport'
import type { ProjectRow } from '../transport/api'

/**
 * Friend DMs, as the rest of the app needs them.
 *
 * Started as the Humans tab's unread count — owner, 2026-08-22: 把未讀信息計算的
 * counting 小紅點放到下面的 Agents 跟 Humans 的 tab 上面，這樣就很容易知道該去哪個
 * 地方看未讀信息.
 *
 * The Agents tab already had its numbers: every project row and every agent row
 * carries an unread count, and activity/feed.ts sums them for the Activity badge.
 * The Humans tab had none — friend rows are a roster, and the DM behind each one
 * was never listed. `GET /conversations?kind=dm` already returns the same
 * `unread` / `last_seq` pair the project rows use, so this is a second reader of
 * an existing answer, not a new count invented on the client.
 *
 * Pure on purpose: the badge, the per-friend marks and the read-cursor advance all
 * read the SAME rows through these functions, so the tab and the row it sends you
 * to cannot disagree about who is unread.
 */

/**
 * The DMs that are still yours. `left`/`archived` drop out for the same reason they
 * do in dynamoGetProjects: a conversation you have left is not in your list, and
 * counting it would light a badge that leads to a row nobody renders.
 */
export function activeDms(rows: ConversationRow[] | undefined): ConversationRow[] {
  return (rows ?? []).filter(
    (c) => c.kind === 'dm' && c.status !== 'left' && c.status !== 'archived',
  )
}

/**
 * The number on the tab.
 *
 * The server caps each row's own count at 99+ (transport.ts), so this is a sum of
 * bounded parts — the tab caps its rendering again, which is display, not arithmetic.
 */
export function dmUnreadTotal(rows: ConversationRow[] | undefined): number {
  let n = 0
  for (const c of activeDms(rows)) n += c.unread ?? 0
  return n
}

/**
 * email → unread, for the marks on the friend rows themselves.
 *
 * A tab badge with nothing behind it is half a mechanism: it says "there is unread
 * mail in here" and then hands you an undifferentiated roster. Keyed by the peer's
 * email because that is the identity the friends list is built on, end to end.
 *
 * A DM created before the server enriched rows with `peer` has no email to key on;
 * it is skipped here and still counted by dmUnreadTotal — the tab may therefore be
 * larger than the marks add up to, which is the honest direction to be wrong in
 * (the mail exists; we cannot say whose).
 */
export function dmUnreadByEmail(rows: ConversationRow[] | undefined): Map<string, number> {
  const by = new Map<string, number>()
  for (const c of activeDms(rows)) {
    const email = c.peer?.email
    if (!email) continue
    const n = c.unread ?? 0
    if (n <= 0) continue
    by.set(email, (by.get(email) ?? 0) + n)
  }
  return by
}

/**
 * email → the DM's last-message preview, for the friend CARDS (owner, 2026-08-27:
 * 讓 Friends 的卡片也如 Agents 卡片一樣，有最後一個對話 block 的 preview). Same
 * server fields the project cards read, keyed the way the friends roster is.
 * `fromHash` set ⇒ an agent spoke (pixelpet face); absent ⇒ a human, who wears their
 * own face from `fromEmail` / `fromPicture` (owner, 2026-09-27: 朋友卡片也一起加上頭像).
 */
export type DmPreview = { text: string; from?: string; fromHash?: string; fromEmail?: string; fromPicture?: string }

export function dmPreviewByEmail(rows: ConversationRow[] | undefined): Map<string, DmPreview> {
  const by = new Map<string, DmPreview>()
  for (const c of activeDms(rows)) {
    const email = c.peer?.email
    if (!email || !c.last_msg_preview) continue
    by.set(email, {
      text: c.last_msg_preview,
      ...(c.last_from ? { from: c.last_from } : {}),
      ...(c.last_from_hash ? { fromHash: c.last_from_hash } : {}),
      ...(c.last_from_email ? { fromEmail: c.last_from_email } : {}),
      ...(c.last_from_picture ? { fromPicture: c.last_from_picture } : {}),
    })
  }
  return by
}

/** The row for one open conversation, so the panel can read its cursor. */
export function dmRowFor(
  rows: ConversationRow[] | undefined,
  convId: string | null | undefined,
): ConversationRow | undefined {
  if (!convId) return undefined
  return activeDms(rows).find((c) => c.conv_id === convId)
}

/**
 * Carry a DM's unread + newest seq onto the row the conversation panel renders.
 *
 * WHY THIS EXISTS AT ALL. useManagedFriendDm resolves an email to a conv_id and
 * nothing else — a friend pair always maps to the same id, so that query is cached
 * forever and deliberately knows no counts. The panel's mark-read effect advances
 * the read cursor to `last_seq`, and the write is a NO-OP without one. So a DM was
 * read on screen and stayed unread everywhere else: light the new badge on top of
 * that and it could never go out. A badge that lies is worse than no badge, which
 * is the rule the project path already states — this is the same rule reaching the
 * one conversation kind that had no list behind it.
 *
 * Fields are assigned only when the list actually has them: with
 * exactOptionalPropertyTypes a missing unread must be ABSENT, not `undefined`, or
 * the row claims a count of nothing.
 */
export function withDmCursor(
  row: ProjectRow | null,
  rows: ConversationRow[] | undefined,
): ProjectRow | null {
  if (!row) return null
  const c = dmRowFor(rows, row.project_id)
  if (!c) return row
  return {
    ...row,
    ...(c.unread !== undefined ? { unread: c.unread } : {}),
    ...(typeof c.last_seq === 'number' ? { last_seq: c.last_seq } : {}),
  }
}

/**
 * DM rows in the shape a ROOM has — owner, 2026-08-22: Project 那個欄位應該可以改名
 * 為 Chat 這樣也包含 friend 1-1 chat.
 *
 * A DM and a project are the same thing to almost everything downstream: a
 * conversation with an id, a title, an unread count and a page of messages. The
 * Activity feed renders both from ProjectRow, so this is the adapter rather than a
 * second code path through the feed — one renderer, one unread rule, one place to fix.
 *
 * `dm_peer` is what makes tapping the row open a DM instead of a project: a friend's
 * address, or — a group-mate who is not a friend has none to give (owner, 2026-10-18:
 * 只有加好友之後 Email 才會互相顯示) — the dm's own conversation id, which opens it just
 * the same (urlState.isDmConv). A row whose peer the server has not enriched at all is
 * DROPPED: it could be listed but not named.
 */
export function dmAsRooms(rows: ConversationRow[] | undefined): ProjectRow[] {
  const out: ProjectRow[] = []
  for (const c of activeDms(rows)) {
    if (!c.peer) continue
    const peer = c.peer.email || c.conv_id
    const row: ProjectRow = {
      project_id: c.conv_id,
      name: (c.peer.name ?? c.peer.email ?? '').trim() || 'Someone',
      dm_peer: peer,
    }
    if (c.unread !== undefined) row.unread = c.unread
    if (typeof c.last_seq === 'number') row.last_seq = c.last_seq
    out.push(row)
  }
  return out
}
